import { useRef, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router';
import { CircleSlash, Copy, MessageSquare, Paperclip } from 'lucide-react';
import type { RequestDetail, RequestEventView, RequestMessageView, RequestStatus } from '@product-portal/shared';
import { Button } from '@/components/ui/button';
import { AttachmentChip } from '@/components/portal/attachment-chip';
import { ImpactTag } from '@/components/portal/impact-tag';
import { Pill } from '@/components/portal/pill';
import { SelectedFiles } from '@/components/portal/selected-files';
import { TypeTag } from '@/components/portal/type-tag';
import { useReply, useRequest, useSubscribeOther, useSubscriberCandidates, useSubscription } from '@/hooks/use-requests';
import { isApiError } from '@/lib/api';
import { formatDate, formatDayMonth, formatDayTime, fullName, shortName } from '@/lib/format';
import { IMPACT_SHORT_LABELS, INTAKE_CHANNEL_LABELS, NO_PRODUCT_LABEL, PRIORITY_LABELS, STATUS_LABELS, TYPE_LABELS } from '@/lib/labels';
import { filesOf } from '@/lib/files';
import { cn, initials } from '@/lib/utils';
import { NotFoundPage } from './not-found-page';

// ---------------------------------------------------------------------------
// Progress bar: five steps, the qualification step has two states.
// ---------------------------------------------------------------------------

type StepState = 'done' | 'half' | 'todo';

const STEP_INDEX: Partial<Record<RequestStatus, number>> = {
  submitted: 0,
  qualifying: 1,
  qualified: 1,
  planned: 2,
  in_progress: 3,
  delivered: 4,
};

/** Date of the last event that moved the request to one of `statuses`. */
function reachedAt(events: RequestEventView[], statuses: RequestStatus[]): string | null {
  const event = [...events].reverse().find((e) => e.field === 'status' && statuses.includes(e.after as RequestStatus));
  return event?.createdAt ?? null;
}

/**
 * Refused or duplicate (ADR-005 §3): a banner replaces the progress bar — the
 * reason of the refusal, or the link to the original request.
 */
function ClosureBanner({ request }: { request: RequestDetail }) {
  const closure = request.closure;
  if (!closure) return null;
  const refused = closure.kind === 'refused';
  return (
    <section
      aria-label={refused ? 'Demande refusée' : 'Demande en doublon'}
      className={cn(
        'flex items-start gap-3.5 rounded-card border px-6 py-4',
        refused ? 'border-status-refused bg-status-refused text-status-refused-foreground' : 'border-border bg-status-duplicate text-status-duplicate-foreground',
      )}
    >
      {refused ? <CircleSlash aria-hidden="true" className="mt-0.5 size-5 shrink-0" /> : <Copy aria-hidden="true" className="mt-0.5 size-5 shrink-0" />}
      <div className="flex flex-col gap-1">
        <strong className="text-sm font-bold">{refused ? 'Demande refusée' : 'Demande rattachée à une demande existante'}</strong>
        {closure.kind === 'refused' ? (
          <p className="m-0 whitespace-pre-line text-sm leading-relaxed text-body-strong">{closure.reason}</p>
        ) : closure.original ? (
          <p className="m-0 text-sm text-body-strong">
            Elle est suivie dans{' '}
            <Link to={`/demandes/${closure.original.id}`} className="font-bold no-underline">
              {closure.original.reference}
              {closure.original.title ? ` « ${closure.original.title} »` : ''}
            </Link>
            .
          </p>
        ) : (
          <p className="m-0 text-sm text-body-strong">La demande d'origine n'est plus disponible.</p>
        )}
      </div>
    </section>
  );
}

function ProgressSteps({ request }: { request: RequestDetail }) {
  const current = STEP_INDEX[request.status];
  if (current === undefined) return <ClosureBanner request={request} />;
  const qualified = request.status !== 'submitted' && request.status !== 'qualifying';
  const steps: { label: string; date: string | null }[] = [
    { label: STATUS_LABELS.submitted, date: request.submittedAt },
    {
      label: qualified ? STATUS_LABELS.qualified : STATUS_LABELS.qualifying,
      date: reachedAt(request.events, qualified ? ['qualified'] : ['qualifying']) ?? reachedAt(request.events, ['qualifying', 'qualified']),
    },
    { label: STATUS_LABELS.planned, date: reachedAt(request.events, ['planned']) },
    { label: STATUS_LABELS.in_progress, date: reachedAt(request.events, ['in_progress']) },
    { label: STATUS_LABELS.delivered, date: reachedAt(request.events, ['delivered']) },
  ];
  const stateOf = (i: number): StepState => (i < current ? 'done' : i > current ? 'todo' : i === 1 && !qualified ? 'half' : 'done');
  return (
    <section aria-label="Avancement" className="rounded-card border border-border bg-card px-6 py-5">
      <ol className="m-0 grid list-none grid-cols-5 gap-2 p-0">
        {steps.map((step, i) => {
          const state = stateOf(i);
          return (
            <li key={step.label + i} className="flex min-w-0 flex-col gap-2" aria-current={i === current ? 'step' : undefined}>
              <div className={cn('h-1.5 rounded-[3px]', state === 'done' ? 'bg-primary' : state === 'half' ? 'bg-progress-half' : 'bg-progress-track')} />
              <span
                className={cn('text-[13px] font-bold', state === 'todo' ? 'text-muted-foreground' : i === current ? 'text-accent-strong' : 'text-foreground')}
              >
                {step.label}
              </span>
              <span className="text-xs text-muted-foreground">{state !== 'todo' && step.date ? formatDayTime(step.date) : '—'}</span>
            </li>
          );
        })}
      </ol>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Timeline: public messages and system events (internal notes never come from the API).
// ---------------------------------------------------------------------------

type TimelineItem = { kind: 'event'; at: string; event: RequestEventView } | { kind: 'message'; at: string; message: RequestMessageView };

function statusLabel(value: unknown): string {
  return typeof value === 'string' && value in STATUS_LABELS ? STATUS_LABELS[value as keyof typeof STATUS_LABELS] : String(value);
}

function EventText({ event, request }: { event: RequestEventView; request: RequestDetail }) {
  if (event.field === 'created') return <span>Demande créée et transmise à l'équipe produit. Référence {request.reference}.</span>;
  if (event.field === 'product') {
    const productId = (event.after as { productId?: string } | null)?.productId;
    return <span>Produit attribué{productId && request.product?.id === productId ? ` : ${request.product.name}` : ''}.</span>;
  }
  return (
    <span>
      Statut : {statusLabel(event.before)} → <strong className="font-bold text-primary">{statusLabel(event.after)}</strong>.
      {event.after === 'qualifying' && " L'équipe produit analyse votre demande."}
    </span>
  );
}

function Timeline({ request }: { request: RequestDetail }) {
  const items: TimelineItem[] = [
    ...request.events.map((event) => ({ kind: 'event' as const, at: event.createdAt, event })),
    ...request.messages.map((message) => ({ kind: 'message' as const, at: message.createdAt, message })),
  ].sort((a, b) => a.at.localeCompare(b.at));
  const lastQuestionId = [...request.messages].reverse().find((m) => m.expectsReply)?.id;

  return (
    <ol className="m-0 flex list-none flex-col gap-5 px-6 py-5">
      {items.map((item) =>
        item.kind === 'event' ? (
          <li key={item.event.id} className="flex items-start gap-3.5">
            <span aria-hidden="true" className="flex size-8 shrink-0 items-center justify-center rounded-full bg-system-avatar text-xs font-bold text-system-avatar-foreground">
              ·
            </span>
            <div className="flex flex-col gap-1 text-[13px] text-muted-foreground">
              <span>
                <strong className="font-bold text-foreground">Système</strong> · {formatDayTime(item.at)}
              </span>
              <EventText event={item.event} request={request} />
            </div>
          </li>
        ) : (
          <li key={item.message.id} className="flex items-start gap-3.5">
            <span
              aria-label={fullName(item.message.author)}
              className="flex size-8 shrink-0 items-center justify-center rounded-full bg-avatar text-xs font-bold text-avatar-foreground"
            >
              {initials(item.message.author.firstName, item.message.author.lastName)}
            </span>
            <div className="flex min-w-0 flex-1 flex-col gap-1.5">
              <span className="text-[13px] text-muted-foreground">
                <strong className="font-bold text-foreground">{shortName(item.message.author)}</strong>
                {/* Role copied on the message when it was sent (ADR-005 §4): display only. */}
                {!item.message.byRequester && (item.message.authorRole === 'po' ? ' · Équipe produit' : item.message.authorRole === 'referent' ? ' · Référent' : '')} ·{' '}
                {formatDayTime(item.at)}
              </span>
              <div className="whitespace-pre-line rounded-card bg-bubble px-3.5 py-3 text-sm leading-[1.55] text-body-strong">{item.message.body}</div>
              {item.message.attachments.length > 0 && (
                <div className="flex flex-wrap gap-2.5">
                  {item.message.attachments.map((a) => (
                    <AttachmentChip key={a.id} attachment={a} />
                  ))}
                </div>
              )}
              {request.awaitingReply && item.message.id === lastQuestionId && (
                <span className="inline-flex items-center gap-1.5 text-xs font-bold text-warning-foreground">
                  <MessageSquare aria-hidden="true" className="size-3.5" strokeWidth={2.2} /> Réponse attendue de votre part
                </span>
              )}
            </div>
          </li>
        ),
      )}
    </ol>
  );
}

function ReplyForm({ requestId }: { requestId: string }) {
  const reply = useReply(requestId);
  const [body, setBody] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const fileInput = useRef<HTMLInputElement>(null);
  return (
    <form
      className="flex flex-col gap-2.5 border-t border-border px-6 pb-5 pt-4"
      onSubmit={(event) => {
        event.preventDefault();
        if (!body.trim()) return;
        reply.mutate(
          { body, files },
          {
            onSuccess: () => {
              setBody('');
              setFiles([]);
            },
          },
        );
      }}
    >
      <label htmlFor="reponse" className="text-[13px] font-bold">
        Répondre
      </label>
      <textarea
        id="reponse"
        rows={3}
        value={body}
        onChange={(event) => setBody(event.target.value)}
        placeholder="Votre réponse est transmise à l'équipe produit et visible par votre référent."
        className="resize-y rounded-control border border-input bg-card p-3 font-[inherit] text-sm leading-normal text-foreground"
      />
      <SelectedFiles files={files} onRemove={(index) => setFiles((current) => current.filter((_, i) => i !== index))} />
      {reply.isError && (
        <p role="alert" className="m-0 text-xs font-bold text-destructive">
          {reply.error.message}
        </p>
      )}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Button type="button" variant="secondary" size="sm" className="min-h-9 font-normal" onClick={() => fileInput.current?.click()}>
          <Paperclip aria-hidden="true" className="size-4" />
          Joindre un fichier
        </Button>
        <input
          ref={fileInput}
          type="file"
          multiple
          className="hidden"
          onChange={(event) => {
            // Copied before the input is reset (see lib/files).
            const picked = filesOf(event.target.files);
            if (picked.length > 0) setFiles((current) => [...current, ...picked]);
            event.target.value = '';
          }}
        />
        <Button type="submit" disabled={reply.isPending || !body.trim()}>
          Envoyer
        </Button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Side blocks
// ---------------------------------------------------------------------------

function SideCard({ title, children, className }: { title: string; children: ReactNode; className?: string }) {
  return (
    <section className={cn('flex flex-col gap-2.5 rounded-card border border-border bg-card px-5 py-4', className)}>
      <h2 className="m-0 text-[15px] font-bold">{title}</h2>
      {children}
    </section>
  );
}

function InformationBlock({ request }: { request: RequestDetail }) {
  const v = request.targetVersion;
  const rows: [string, ReactNode][] = [
    ['Demandeur', fullName(request.requester)],
    ['Équipe métier', request.team.name],
    ['Référent', request.referents.length > 0 ? request.referents.map(fullName).join(', ') : '—'],
    ['Produit', request.product?.name ?? NO_PRODUCT_LABEL],
    ['Type', TYPE_LABELS[request.type]],
    ['Impact déclaré', IMPACT_SHORT_LABELS[request.impactDeclared]],
    ['Priorité produit', request.priority ? PRIORITY_LABELS[request.priority as keyof typeof PRIORITY_LABELS] : '—'],
    ['Version cible', v ? `${request.product?.name ?? ''} ${v.number}${v.plannedDate ? ` · ${formatDayMonth(v.plannedDate)}` : ''}`.trim() : '—'],
  ];
  return (
    <SideCard title="Informations">
      <dl className="m-0 grid grid-cols-2 gap-x-3 gap-y-2.5 text-[13px]">
        {rows.map(([label, value]) => (
          <div key={label} className="contents">
            <dt className="text-muted-foreground">{label}</dt>
            <dd className="m-0 font-bold">{value}</dd>
          </div>
        ))}
        {/* Always present; « — » until the request is delivered. */}
        <dt className="text-muted-foreground">Date de livraison</dt>
        {request.deliveredAt ? (
          <dd className="m-0 font-bold">{formatDate(request.deliveredAt)}</dd>
        ) : (
          <dd className="m-0 font-normal text-muted-foreground">
            — <span className="sr-only">Non livrée</span>
          </dd>
        )}
        <dt className="text-muted-foreground">Créée le</dt>
        <dd className="m-0 font-bold">{formatDate(request.createdAt)}</dd>
        {/* ADR-004: only when a PO typed the request for the requester. */}
        {request.enteredBy && (
          <>
            <dt className="text-muted-foreground">Saisie par</dt>
            <dd className="m-0 font-bold">
              {shortName(request.enteredBy.person)} · {INTAKE_CHANNEL_LABELS[request.enteredBy.channel].toLowerCase()}
            </dd>
          </>
        )}
        <dt className="text-muted-foreground">Mise à jour</dt>
        <dd className="m-0 font-bold">{formatDate(request.lastActivityAt)}</dd>
      </dl>
    </SideCard>
  );
}

/** A PO or a Référent of the request's team subscribes a colleague (2026-10-23). */
function SubscribeSomeone({ requestId }: { requestId: string }) {
  const [q, setQ] = useState('');
  const candidates = useSubscriberCandidates(requestId, q);
  const subscribe = useSubscribeOther(requestId);
  return (
    <div className="flex flex-col gap-1.5 border-t border-border pt-3">
      <label htmlFor="abonner" className="text-[13px] font-bold">
        Abonner une personne
      </label>
      <input id="abonner" type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nom ou adresse" className="min-h-9 rounded-control border border-border bg-card px-3 font-[inherit] text-[13px]" />
      {q.trim().length >= 2 && (candidates.data ?? []).length === 0 && !candidates.isFetching && <span className="text-xs text-muted-foreground">Personne à abonner.</span>}
      {(candidates.data ?? []).map((p) => (
        <button
          key={p.id}
          type="button"
          disabled={subscribe.isPending}
          onClick={() => subscribe.mutate(p.id, { onSuccess: () => setQ('') })}
          className="flex cursor-pointer items-center justify-between gap-2 rounded-control border border-border bg-card px-3 py-1.5 text-left font-[inherit] text-[13px] hover:bg-accent"
        >
          <span>
            <strong>{fullName(p)}</strong>
            {p.teamName && <span className="text-muted-foreground"> · {p.teamName}</span>}
          </span>
          <span className="text-xs font-bold text-primary">Abonner</span>
        </button>
      ))}
      {subscribe.error && <span className="text-xs font-bold text-destructive">{subscribe.error.message}</span>}
    </div>
  );
}

function SubscribersBlock({ request, isRequester }: { request: RequestDetail; isRequester: boolean }) {
  const subscription = useSubscription(request.id);
  const followers = [
    { ...request.requester, teamName: request.team.name },
    ...request.subscribers.filter((s) => s.id !== request.requester.id),
  ];
  const names = followers.map((f) => `${shortName(f)}${f.teamName && f.teamName !== request.team.name ? ` (${f.teamName})` : ''}`);
  return (
    <SideCard title="Abonnés">
      <p className="m-0 text-[13px] text-foreground-secondary">
        {followers.length === 1 ? '1 personne suit' : `${followers.length} personnes suivent`} cette demande : {names.join(', ')}.
      </p>
      {request.linkedDuplicates.length > 0 && (
        <span className="text-xs text-muted-foreground">
          Demandes rattachées : {request.linkedDuplicates.map((d) => `${d.reference} (doublon)`).join(', ')}
        </span>
      )}
      {!isRequester && (
        <Button
          type="button"
          variant="secondary"
          size="sm"
          className="self-start"
          disabled={subscription.isPending}
          onClick={() => subscription.mutate(!request.viewer.isSubscribed)}
        >
          {request.viewer.isSubscribed ? 'Ne plus suivre cette demande' : 'Suivre cette demande'}
        </Button>
      )}
      {request.viewer.canSubscribeOthers && <SubscribeSomeone requestId={request.id} />}
    </SideCard>
  );
}

/** 03 · Suivi d'une demande. */
export function RequestPage({ currentUserId }: { currentUserId: string }) {
  const { id = '' } = useParams();
  const query = useRequest(id);
  const [copied, setCopied] = useState(false);

  if (query.isPending) return <p className="m-0 text-sm text-muted-foreground">Chargement…</p>;
  if (query.isError) {
    if (isApiError(query.error, 404) || isApiError(query.error, 400)) return <NotFoundPage />;
    return <p className="m-0 text-sm text-muted-foreground">La demande n'a pas pu être chargée.</p>;
  }
  const request = query.data;
  const isRequester = request.requester.id === currentUserId;
  const link = request.url ?? request.cmsArticleUrl;

  return (
    <>
      <div className="flex flex-col gap-2.5">
        <Link to="/demandes" className="text-[13px] no-underline">
          ← Mes demandes
        </Link>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex min-w-0 flex-col gap-2">
            <div className="flex flex-wrap items-center gap-2.5">
              <span className="text-[13px] text-muted-foreground tabular-nums">{request.reference}</span>
              <TypeTag type={request.type} />
              <Pill className="bg-muted text-foreground-secondary">{request.product?.name ?? NO_PRODUCT_LABEL}</Pill>
              <ImpactTag impact={request.impactDeclared} />
            </div>
            <h1 className="m-0 text-2xl font-black tracking-[-0.01em]">{request.title}</h1>
          </div>
          <div className="flex flex-wrap gap-2.5">
            <Button
              type="button"
              variant="secondary"
              className="text-[13px]"
              onClick={() => {
                void navigator.clipboard?.writeText(window.location.href).then(() => setCopied(true));
              }}
            >
              {copied ? 'Lien copié' : 'Partager le lien'}
            </Button>
            {/* « Clôturer ma demande » (mock-up 03) is not implemented: no closing transition is documented for the requester (see CLAUDE.md). */}
          </div>
        </div>
      </div>

      <ProgressSteps request={request} />

      <div className="flex flex-wrap items-start gap-6">
        <div className="flex min-w-0 flex-[999_1_560px] flex-col gap-6">
          <section className="flex flex-col gap-3 rounded-card border border-border bg-card px-6 py-5">
            <h2 className="m-0 text-[15px] font-bold">Description</h2>
            <p className="m-0 whitespace-pre-line text-sm leading-relaxed text-body-strong">{request.description}</p>
            {link && (
              <div className="flex flex-wrap gap-2.5 text-[13px]">
                <span className="text-muted-foreground">URL :</span>
                <a href={link} target="_blank" rel="noreferrer noopener" className="break-all no-underline">
                  {link.replace(/^https?:\/\//, '')}
                </a>
              </div>
            )}
            {request.clientEmail && (
              <div className="flex flex-wrap gap-2.5 text-[13px]">
                <span className="text-muted-foreground">Client concerné :</span>
                <span>
                  {request.clientEmail} · NCCT {request.clientNcct}
                </span>
              </div>
            )}
            {request.attachments.length > 0 && (
              <div className="flex flex-wrap gap-2.5">
                {request.attachments.map((a) => (
                  <AttachmentChip key={a.id} attachment={a} />
                ))}
              </div>
            )}
          </section>

          <section className="flex flex-col rounded-card border border-border bg-card">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-6 py-4">
              <h2 className="m-0 text-[15px] font-bold">Échanges avec l'équipe produit</h2>
              <span className="text-xs text-muted-foreground">Les échanges internes de l'équipe ne sont pas affichés</span>
            </div>
            <Timeline request={request} />
            {request.viewer.canReply && <ReplyForm requestId={request.id} />}
          </section>
        </div>

        <aside className="flex min-w-0 flex-[1_1_300px] flex-col gap-6">
          <InformationBlock request={request} />

          <SideCard title="Notifications">
            {/* TODO(OPEN-QUESTION notification-preferences): no column stores these preferences; every email is sent. */}
            <label className="flex min-h-8 items-center gap-2.5 text-[13px]">
              <input type="checkbox" checked disabled readOnly className="m-0" /> Email à chaque changement de statut
            </label>
            <label className="flex min-h-8 items-center gap-2.5 text-[13px]">
              <input type="checkbox" checked disabled readOnly className="m-0" /> Email à chaque réponse de l'équipe
            </label>
            <span className="text-xs text-muted-foreground">Préférences non modifiables pour le moment.</span>
          </SideCard>

          <SubscribersBlock request={request} isRequester={isRequester} />

          {/* Sent by the API to the PO only; absent from every other response. */}
          {request.productView && (
            <section className="flex flex-col gap-1.5 rounded-card border border-accent-border bg-accent px-5 py-3.5">
              <span className="text-xs font-bold text-accent-strong">Vue équipe produit</span>
              <span className="text-[13px] text-body-strong">
                {request.productView.jiraKey
                  ? `Ticket Jira ${request.productView.jiraKey}${request.productView.lastKnownJiraStatus ? ` · ${request.productView.lastKnownJiraStatus}` : ''}`
                  : 'Aucun ticket Jira lié'}
              </span>
              <span className="text-xs text-muted-foreground">Bloc visible uniquement pour le rôle Produit.</span>
            </section>
          )}
        </aside>
      </div>
    </>
  );
}
